import { useCallback, useEffect, useRef, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ShoppingBag } from 'lucide-react';

const CATEGORY_LABELS = {
  freschi:   'Prodotti Freschi',
  specialita:'Specialità Locali',
  conserve:  'Conserve Artigianali',
};

const CATEGORY_COLORS = {
  freschi:   'text-emerald-600 bg-emerald-50',
  specialita:'text-amber-600 bg-amber-50',
  conserve:  'text-red-600 bg-red-50',
};

function getCatLabel(cat) { return CATEGORY_LABELS[cat] || cat; }
function getCatColor(cat) { return CATEGORY_COLORS[cat] || 'text-primary bg-primary/5'; }

function toOdd(arr) {
  if (!Array.isArray(arr) || !arr.length) return arr || [];
  return arr.length % 2 === 0 ? arr.slice(0, arr.length - 1) : arr;
}

function ProductCard({ product, isCenter }) {
  const hasPrice = product.price > 0;
  const unavailable = product.available === false;

  return (
    <Link to="/negozio" className="block group text-center">
      {/* Solo cerchio — nessuna card bianca */}
      <div className="flex justify-center mb-3">
        <div className={`relative rounded-full overflow-hidden transition-transform duration-500 group-hover:scale-105 flex-shrink-0 ${
          isCenter ? 'w-56 h-56 md:w-72 md:h-72' : 'w-44 h-44 md:w-56 md:h-56'
        } ${isCenter ? 'ring-4 ring-primary/30 shadow-2xl' : 'ring-3 ring-gray-200 shadow-lg'}`}>
            {/* Sfondo blur */}
            <img
              src={product.image_url} alt="" aria-hidden
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover blur-xl scale-110 opacity-50 pointer-events-none"
              draggable={false}
            />
            {/* Foto principale */}
            <img
              src={product.image_url} alt={product.name}
              loading="lazy"
              className="relative z-10 w-full h-full object-cover"
              draggable={false}
              onError={e => { e.target.style.opacity = '0.3'; }}
            />
            {/* Overlay esaurito */}
            {unavailable && (
              <div className="absolute inset-0 z-20 bg-black/50 flex items-center justify-center">
                <span className="text-white text-[10px] font-body font-bold">Esaurito</span>
              </div>
            )}
          </div>
      </div>

      {/* Testo sotto cerchio */}
      <span className={`inline-block text-[10px] uppercase tracking-widest font-body font-semibold px-2 py-0.5 rounded-full mb-1 ${getCatColor(product.category)}`}>
        {getCatLabel(product.category)}
      </span>
      <h3 className={`font-heading font-semibold text-text-DEFAULT leading-tight ${isCenter ? 'text-base md:text-lg' : 'text-sm'}`}>
        {product.name}
      </h3>
      {isCenter && (
        <>
          <p className="font-body text-text-muted text-xs leading-snug line-clamp-2 mt-1 mb-2 max-w-xs mx-auto">
            {product.description}
          </p>
          <div className="flex items-center justify-center gap-3">
            {hasPrice
              ? <span className="font-heading font-bold text-sm text-primary">€ {Number(product.price).toFixed(2).replace('.', ',')}</span>
              : <span className="font-body text-xs text-text-muted italic">Su richiesta</span>
            }
            <span className="inline-flex items-center gap-1 text-primary text-xs font-body font-semibold">
              <ShoppingBag className="w-3 h-3" /> Scopri
            </span>
          </div>
        </>
      )}
    </Link>
  );
}

export default function ProductCarousel3D({ products: rawProducts = [] }) {
  const products = toOdd(rawProducts);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true, align: 'center', skipSnaps: false, dragFree: false, containScroll: false,
  });

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps]     = useState([]);
  const autoplayRef = useRef(null);

  const stopAutoplay  = useCallback(() => {
    if (autoplayRef.current) clearInterval(autoplayRef.current);
  }, []);

  const startAutoplay = useCallback(() => {
    stopAutoplay();
    if (!emblaApi) return;
    autoplayRef.current = setInterval(() => emblaApi.scrollNext(), 4000);
  }, [emblaApi, stopAutoplay]);

  const onSelect = useCallback((api) => setSelectedIndex(api.selectedScrollSnap()), []);

  useEffect(() => {
    if (!emblaApi) return;
    setScrollSnaps(emblaApi.scrollSnapList());
    onSelect(emblaApi);
    emblaApi.on('reInit', onSelect);
    emblaApi.on('select', onSelect);
    emblaApi.on('pointerDown', stopAutoplay);
    emblaApi.on('pointerUp', startAutoplay);
    startAutoplay();
    return () => {
      stopAutoplay();
      emblaApi.off('reInit', onSelect);
      emblaApi.off('select', onSelect);
      emblaApi.off('pointerDown', stopAutoplay);
      emblaApi.off('pointerUp', startAutoplay);
    };
  }, [emblaApi, onSelect, startAutoplay, stopAutoplay]);

  const getSlideProps = (index) => {
    const n = products.length;
    let dist = Math.abs(index - selectedIndex);
    if (dist > Math.floor(n / 2)) dist = n - dist;
    if (dist === 0) return { scale: 1,    opacity: 1,    zIndex: 20 };
    if (dist === 1) return { scale: 0.88, opacity: 0.75, zIndex: 10 };
                   return { scale: 0.70, opacity: 0.30, zIndex: 1  };
  };

  if (!products.length) return null;

  return (
    <div className="relative select-none px-10 sm:px-12">
      {/* Viewport */}
      <div ref={emblaRef} className="overflow-hidden cursor-grab active:cursor-grabbing py-4">
        <div className="flex items-center">
          {products.map((product, index) => {
            const { scale, opacity, zIndex } = getSlideProps(index);
            const isCenter = index === selectedIndex;
            return (
              <div key={product.id} className="flex-none w-[72%] sm:w-[48%] md:w-[34%] lg:w-[30%] px-1" style={{ zIndex }}>
                <div style={{
                  transform: `scale(${scale})`,
                  opacity,
                  transition: 'transform 0.45s cubic-bezier(0.25,1,0.5,1), opacity 0.45s ease',
                  transformOrigin: 'center center',
                }}>
                  <ProductCard product={product} isCenter={isCenter} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Frecce */}
      {[
        { side: 'left',  action: () => { stopAutoplay(); emblaApi?.scrollPrev(); }, Icon: ChevronLeft,  label: 'Precedente' },
        { side: 'right', action: () => { stopAutoplay(); emblaApi?.scrollNext(); }, Icon: ChevronRight, label: 'Successivo' },
      ].map(({ side, action, Icon, label }) => (
        <button
          key={side}
          onClick={action}
          aria-label={label}
          className={`absolute ${side}-0.5 top-1/2 -translate-y-1/2 z-30 w-9 h-9 bg-white/95 backdrop-blur-sm shadow-lg rounded-full flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-all duration-200 border border-primary/10`}
        >
          <Icon className="w-4 h-4" />
        </button>
      ))}

      {/* Indicatori */}
      <div className="flex justify-center items-center gap-1.5 mt-1">
        {scrollSnaps.map((_, i) => (
          <button
            key={i}
            onClick={() => { stopAutoplay(); emblaApi?.scrollTo(i); }}
            aria-label={`Prodotto ${i + 1}`}
            className={`rounded-full transition-all duration-300 ${
              i === selectedIndex ? 'w-5 h-1.5 bg-primary' : 'w-1.5 h-1.5 bg-primary/25 hover:bg-primary/50'
            }`}
          />
        ))}
      </div>
    </div>
  );
}
